import React from 'react'
import { Button, Input, Label, Modal, Textarea, Checkbox } from '@/shared/ui'
import type { Plan, PlanInterval } from '../types'
import { PLAN_INTERVALS, PLAN_INTERVAL_LABEL } from '../types'
import { upsertPlan } from '../services/planService'
import PlanOpportunityLimitsForm from './PlanOpportunityLimitsForm'
import PlanAssetAccessForm from './PlanAssetAccessForm'

type Props = {
  open: boolean
  onClose: () => void
  onSaved: () => void
  initial?: Partial<Plan>
}

export default function PlanFormModal({ open, onClose, onSaved, initial }: Props) {
  const [name, setName] = React.useState(initial?.name ?? '')
  const [slug, setSlug] = React.useState(initial?.slug ?? '')
  const [description, setDescription] = React.useState(initial?.description ?? '')
  const [features, setFeatures] = React.useState((initial?.features ?? []).join('\n'))
  const [discountPercent, setDiscountPercent] = React.useState<number>(() => {
    const pct = (initial as any)?.meta?.discount_percent
    return typeof pct === 'number' ? pct : 0
  })
  const [discountRecurring, setDiscountRecurring] = React.useState<boolean>(() => {
    const rec = (initial as any)?.meta?.discount_recurrence
    return rec ? String(rec).toLowerCase() !== 'one_time' : true
  })
  const [amountCents, setAmountCents] = React.useState<number>(initial?.amount_cents ?? 0)
  const [currency, setCurrency] = React.useState((initial?.currency ?? 'BRL').toString().toUpperCase())
  const [priceText, setPriceText] = React.useState<string>('')
  const [interval, setInterval] = React.useState<PlanInterval>(initial?.interval ?? 'month')
  const [visible, setVisible] = React.useState<boolean>(!!initial?.visible)
  const [active, setActive] = React.useState<boolean>(initial?.active !== false)
  const [assetsSummary, setAssetsSummary] = React.useState<string>(() => {
    try {
      const meta: any = (initial as any)?.meta || {}
      return typeof meta.assets_summary === 'string' ? meta.assets_summary : ''
    } catch {
      return ''
    }
  })
  const [assetsLinkLabel, setAssetsLinkLabel] = React.useState<string>(() => {
    try {
      const meta: any = (initial as any)?.meta || {}
      return typeof meta.assets_link_label === 'string' ? meta.assets_link_label : ''
    } catch {
      return ''
    }
  })
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!open) return
    setName(initial?.name ?? '')
    setSlug(initial?.slug ?? '')
    setDescription(initial?.description ?? '')
    setFeatures((initial?.features ?? []).join('\n'))
    // Desconto
    try {
      const meta: any = (initial as any)?.meta || {}
      const pct = typeof meta.discount_percent === 'number' ? meta.discount_percent : 0
      setDiscountPercent(pct)
      const rec = meta.discount_recurrence ? String(meta.discount_recurrence).toLowerCase() : 'recurring'
      setDiscountRecurring(rec !== 'one_time')
      setAssetsSummary(typeof meta.assets_summary === 'string' ? meta.assets_summary : '')
      setAssetsLinkLabel(typeof meta.assets_link_label === 'string' ? meta.assets_link_label : '')
    } catch {
      setDiscountPercent(0)
      setDiscountRecurring(true)
      setAssetsSummary('')
      setAssetsLinkLabel('')
    }
    setAmountCents(initial?.amount_cents ?? 0)
    setCurrency((initial?.currency ?? 'BRL').toString().toUpperCase())
    try {
      const cents = initial?.amount_cents ?? 0
      setPriceText(formatCurrencyFromCents(cents, (initial?.currency || 'BRL').toString().toUpperCase()))
    } catch { setPriceText('') }
    setInterval(initial?.interval ?? 'month')
    setVisible(!!initial?.visible)
    setActive(initial?.active !== false)
    setSaving(false)
    setError(null)
  }, [open, initial])

  function formatCurrencyFromCents(cents: number, curr: string): string {
    const value = Math.max(0, Number(cents || 0)) / 100
    try { return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: curr as any }).format(value) }
    catch { return `R$ ${value.toFixed(2).replace('.',',')}` }
  }

  function parseMaskedToCents(text: string): number {
    const digits = String(text || '').replace(/\D/g, '')
    if (!digits) return 0
    // interpreta sempre como centavos
    const n = parseInt(digits, 10)
    return Number.isFinite(n) ? n : 0
  }

  function onPriceChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value
    const cents = parseMaskedToCents(raw)
    setAmountCents(cents)
    setPriceText(formatCurrencyFromCents(cents, currency))
  }

  function onCurrencyChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const curr = e.target.value
    setCurrency(curr)
    setPriceText(formatCurrencyFromCents(amountCents, curr))
  }

  function toSlug(v: string) {
    return v.normalize('NFKD').replace(/[^a-zA-Z0-9]+/g, '-').replace(/(^-|-$)+/g, '').toLowerCase()
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      setSaving(true)
      const featureList = features
        .split(/\n+/)
        .map((s) => s.trim())
        .filter(Boolean)

      // Envelope de features com SSOT de config adicional (desconto)
      const featuresEnvelope: any = {
        list: featureList,
      }
      const pct = Math.max(0, Math.floor(Number(discountPercent) || 0))
      if (pct > 0) {
        featuresEnvelope.discount_percent = pct
        featuresEnvelope.discount_recurrence = discountRecurring ? 'recurring' : 'one_time'
      }
      const assetsSummaryText = (assetsSummary || '').trim()
      const assetsLinkText = (assetsLinkLabel || '').trim()
      if (assetsSummaryText) featuresEnvelope.assets_summary = assetsSummaryText
      if (assetsLinkText) featuresEnvelope.assets_link_label = assetsLinkText

      const payload = {
        id: initial?.id,
        slug: (slug || toSlug(name)).trim(),
        name: name.trim(),
        description: description || undefined,
        features: featuresEnvelope,
        amount_cents: Math.max(0, Math.floor(Number(amountCents) || 0)),
        currency,
        interval,
        visible,
        active,
      }
      await upsertPlan(payload)
      onSaved()
      onClose()
    } catch (err: any) {
      setError(err?.message ?? 'Falha ao salvar plano')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={open} onOpenChange={(v)=>{ if(!v) onClose() }} className="p-6 sm:p-8">
      <div className="mb-4 text-xl font-semibold">{initial?.id ? 'Editar plano' : 'Novo plano'}</div>
      <form className="grid gap-4 sm:grid-cols-2" onSubmit={onSubmit}>
        {error && <div className="sm:col-span-2 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>}
        <div className="space-y-1.5">
          <Label>Nome</Label>
          <Input value={name} onChange={(e)=>{ setName(e.target.value); if(!slug) setSlug(toSlug(e.target.value)) }} placeholder="ex.: Pro" required />
        </div>
        <div className="space-y-1.5">
          <Label>Slug</Label>
          <Input value={slug} onChange={(e)=> setSlug(toSlug(e.target.value))} placeholder="ex.: pro" required />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Descrição</Label>
          <Textarea value={description ?? ''} onChange={(e)=> setDescription(e.target.value)} placeholder="Resumo do plano" />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Recursos (1 por linha)</Label>
          <Textarea value={features} onChange={(e)=> setFeatures(e.target.value)} placeholder="Acesso a ...\nPrioridade ..." rows={5} />
        </div>
        <div className="space-y-3 sm:col-span-2">
          <div className="space-y-1.5">
            <Label>Resumo de moedas na Home (opcional)</Label>
            <Input
              value={assetsSummary}
              onChange={(e)=> setAssetsSummary(e.target.value)}
              placeholder="Ex.: 5 moedas monitoradas"
            />
            <p className="text-xs text-foreground/70">
              Este texto aparece junto aos recursos do plano na Home, antes do link para as moedas.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>Texto do link para moedas (Home)</Label>
            <Input
              value={assetsLinkLabel}
              onChange={(e)=> setAssetsLinkLabel(e.target.value)}
              placeholder="Clique e veja as moedas do plano"
            />
            <p className="text-xs text-foreground/70">
              O link abrirǭ um modal na Home listando as moedas configuradas em &quot;Moedas por plano (Oportunidades)&quot;.
            </p>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Valor</Label>
          <Input type="text" inputMode="numeric" placeholder="R$ 0,00" value={priceText} onChange={onPriceChange} />
        </div>
        <div className="space-y-1.5">
          <Label>Moeda</Label>
          <select className="h-12 w-full rounded-md border border-input bg-background px-3 text-sm" value={currency} onChange={onCurrencyChange}>
            <option value="BRL">BRL</option>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <Label>Intervalo</Label>
          <select className="h-12 w-full rounded-md border border-input bg-background px-3 text-sm" value={interval} onChange={(e)=> setInterval(e.target.value as PlanInterval)}>
            {PLAN_INTERVALS.map((itv) => (
              <option key={itv} value={itv}>{PLAN_INTERVAL_LABEL[itv]}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label>Desconto (%)</Label>
          <Input type="number" min="0" step="1" placeholder="ex.: 10 para 10%" value={discountPercent} onChange={(e)=> setDiscountPercent(Number(e.target.value)||0)} />
          <div className="flex items-center gap-2 pt-1">
            <Checkbox checked={discountRecurring} onChange={(e)=> setDiscountRecurring((e.target as HTMLInputElement).checked)} />
            <span className="text-sm">Aplicar em todas as cobranças</span>
          </div>
          <p className="text-xs text-foreground/70">Se desligado, aplica apenas na primeira cobrança (one-time).</p>
        </div>
        <div className="space-y-1.5">
          <Label>Visível na Home</Label>
          <div className="flex items-center gap-2"><Checkbox checked={visible} onChange={(e)=> setVisible((e.target as HTMLInputElement).checked)} /> <span className="text-sm">Visível</span></div>
        </div>
        <div className="space-y-1.5">
          <Label>Ativo</Label>
          <div className="flex items-center gap-2"><Checkbox checked={active} onChange={(e)=> setActive((e.target as HTMLInputElement).checked)} /> <span className="text-sm">Ativo</span></div>
        </div>
        <div className="sm:col-span-2 mt-2 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Salvar plano'}</Button>
        </div>
      </form>
      {/* Limites de Oportunidade (salvos separadamente para evitar sobreescrever features) */}
      <PlanOpportunityLimitsForm plan={initial?.id ? (initial as any) : undefined} onChanged={onSaved} />
      {/* Regras de moedas por plano (Oportunidades + Web Push) */}
      <PlanAssetAccessForm plan={initial?.id ? (initial as any) : undefined} onChanged={onSaved} />
    </Modal>
  )
}
